import type {} from "react-select/base";
import React, { useState, useRef, useEffect } from "react";
import styles from "./FormInstance.module.scss";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import batchImageRollUpload from "utils/batchImageRollUpload";
import getFullFormInstanceForPhotolog from "utils/getFullFormInstanceForPhotolog";
import { FormGroup, FormInstance as FormInstanceType } from "db/definitions";
import {
	Button,
	DialogContainer,
	DropdownSelect,
	Modal,
	ModalFooter,
	ModalHeading,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	ModelSubHeading,
	ProgressMetadata,
	TextFieldWrapper,
} from "@factorearth/component-library";
import { FiChevronDown, FiChevronUp, FiX } from "react-icons/fi";
import useCustomForm from "hooks/useCustomForm";

interface UploadConfigModalProps {
	colorPalette: Colors;
	initialFormInstanceData: FormInstanceType;
	initialFormData: FormData;
	uploadImagesCallback: any;
	setProgress: (newProgress: Partial<ProgressMetadata>) => void;
}

const UploadConfigModal = (props: UploadConfigModalProps) => {
	const { colorPalette, initialFormInstanceData, uploadImagesCallback, initialFormData, setProgress } = props;
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<Array<{ id: string; label: string; value: string }>>([]);
	const [fieldToMatch, setFieldToMatch] = useState([{ label: "None", value: "NONE", id: "" }]);
	const inputRef = useRef<HTMLInputElement>(null);

	const [imageCount, setImageCount] = useState(0);

	const { values, handleChange, handleSubmit } = useCustomForm(initialFormData, uploadImagesCallback, setProgress, setImageCount, imageCount);

	const handleFieldsToMatch = (e: any) => {
		setFieldToMatch([e]);
		e.target = {};
		e.target.name = "fieldToMatch";
		e.target.id = "fieldToMatch";
		e.target.value = e.value;
		handleChange(e);
	};

	const handleSubmitPhotos = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setProgress({
			value: 0,
			variant: "info",
			message: "Images uploading...",
			title: "Uploading Images",
			canClose: false,
			show: true,
		});
		handleSubmit();
		setOpen(false);
	};

	useEffect(() => {
		const buildFieldTemplateOptions = () => {
			let imageRollField;
			const final = [{ label: "None", value: "NONE", id: "" }];
			if (!initialFormInstanceData.formTemplate || !initialFormInstanceData.formTemplate.fieldTemplates) return final;
			for (let i = 0; i < initialFormInstanceData.formTemplate.fieldTemplates.items?.length; i++) {
				const parentField = initialFormInstanceData?.formTemplate.fieldTemplates.items[i];
				if (parentField.inputType === "imageroll") {
					imageRollField = parentField;
					break;
				}
			}
			if (imageRollField && imageRollField.tableFields) {
				for (let i = 0; i < imageRollField.tableFields.items.length; i++) {
					const photoDataField = imageRollField.tableFields.items[i];
					const option = {
						id: photoDataField.id,
						label: photoDataField.header,
						value: photoDataField.id,
					};
					final.push(option);
				}
			}
			return final;
		};
		setOptions(buildFieldTemplateOptions());
	}, []);

	// Handle escape key to close modal and prevent click-through
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape" && open) {
				event.preventDefault();
				event.stopPropagation();
				setOpen(false);
			}
		};

		if (open) {
			document.addEventListener("keydown", handleKeyDown);
			// Prevent body scroll when modal is open
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "auto";
		};
	}, [open]);

	const handleBrowseClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.stopPropagation();
		if (inputRef && inputRef.current) inputRef.current.click();
	};

	return (
		<div style={{ display: "flex" }}>
			<Button
				colorPalette={colorPalette as Colors & string}
				label="Upload"
				variant="outlined"
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}
			/>
			<Modal colorPalette={colorPalette} open={open} setOpen={setOpen}>
				<DialogContainer onClick={(e) => e.stopPropagation()}>
					<ModalTitleContainer>
						<ModalTitle>Upload Your Images</ModalTitle>
						<FiX
							onClick={(e) => {
								e.stopPropagation();
								setOpen(false);
							}}
							size="24"
						/>
					</ModalTitleContainer>
					<ModelSubHeading>Specify the field you wish to use for matching image files to their respective image records.</ModelSubHeading>
					<ModalInputBox>
						<ModalHeading>Select a Field to Match:</ModalHeading>
						<DropdownSelect
							options={options}
							onChange={handleFieldsToMatch}
							placeholder="Select a Field"
							value={fieldToMatch}
							colorPalette={colorPalette}
							tabIndex={-1}
						/>
					</ModalInputBox>
					<ModelSubHeading>
						Additionally, please provide the camera-generated prefix for each image so that we can efficiently remove it.
					</ModelSubHeading>
					<ModalInputBox>
						<ModalHeading>Camera Prefix:</ModalHeading>
						<TextFieldWrapper
							type="text"
							name="prefix"
							value={values.prefix}
							colorPalette={colorPalette}
							placeholder="e.g.: IMG, DSC, CANON"
							onChange={handleChange as React.ChangeEventHandler<HTMLInputElement>}
							tabIndex={-1}
						/>
					</ModalInputBox>
					<ModalFooter>
						<Button colorPalette={colorPalette} variant="filled" label={`${imageCount} Upload`} onClick={handleBrowseClick}>
							<input
								type="file"
								accept="image/jpeg, image/png, image/webp, image/gif"
								name="files"
								multiple
								ref={inputRef}
								onChange={handleChange as React.ChangeEventHandler<HTMLInputElement>}
								id={`files`}
								className={styles.fileUpload}
								style={{ display: "none" }}
							/>
						</Button>
						<Button colorPalette={colorPalette} variant="outlined" label="Submit" onClick={handleSubmitPhotos} disabled={!imageCount} />
						<Button
							colorPalette={colorPalette}
							variant="destructive"
							label="Cancel"
							onClick={(e) => {
								e.stopPropagation();
								setOpen(false);
							}}
						/>
					</ModalFooter>
				</DialogContainer>
			</Modal>
		</div>
	);
};

interface FormData {
	prefix: string;
	fieldToMatch: string;
	files: FileList | File[];
}

interface Props {
	initialFormInstanceData: FormInstanceType;
	updateObj: any;
	children: React.JSX.Element[];
	online: boolean;
	formGroup: FormGroup | null;
	colorPalette: Colors;
	id: string;
	setProgress: (newProgress: Partial<ProgressMetadata>) => void;
}

function FormInstance(props: Props) {
	const { formGroup, initialFormInstanceData, updateObj, children, online, colorPalette, id, setProgress } = props;
	const [expanded, setExpanded] = useState(formGroup?.formOrder.length === 1 ? true : false);

	const instanceRef = useRef(null);
	useEffect(() => {
		if (updateObj && updateObj === initialFormInstanceData.id) {
			//@ts-ignore
			instanceRef.current.scrollIntoView({ block: "start", behavior: "smooth", inline: "center" });
		}
	}, [updateObj]);

	function toggleExpand() {
		setExpanded(!expanded);
	}

	const initialFormData: FormData = {
		prefix: "",
		fieldToMatch: "",
		files: [],
	};

	async function uploadImagesCallback(data: { fieldToMatch: string; files: FileList | File[]; prefix: string }) {
		if (data.files && formGroup) {
			const updatedFormInstance = await getFullFormInstanceForPhotolog(initialFormInstanceData.id, online);
			const batchImageObj = {
				config: data,
				formInstance: updatedFormInstance,
				formGroup,
			};
			await batchImageRollUpload(batchImageObj, online);
		}
	}

	const index = formGroup?.formOrder.indexOf(id);

	return (
		<div className={`${styles.wrapper}`} ref={instanceRef}>
			<div
				className={expanded ? styles.header : styles.headerCollapsed}
				onClick={toggleExpand}
				data-test="form-instance-accordion"
				style={{
					padding: "24px",
					backgroundColor: index && (index + 1) % 2 === 0 ? colorPalette.background.tertiary : colorPalette.background.primary,
				}}
				ref={instanceRef}>
				<div style={{ color: colorPalette.text.primary }} className={styles.headerTemplate} data-test="form-template-name">
					{initialFormInstanceData.formTemplate?.versionName
						? `${initialFormInstanceData.formTemplate?.template} - ${initialFormInstanceData.formTemplate?.versionName}`
						: initialFormInstanceData.formTemplate?.template || "Loading Template"}
					{formGroup?.type === "photolog" && online && (
						<UploadConfigModal
							colorPalette={colorPalette as Colors & string}
							initialFormInstanceData={initialFormInstanceData}
							uploadImagesCallback={uploadImagesCallback}
							initialFormData={initialFormData}
							setProgress={setProgress}
						/>
					)}
				</div>
				<div id="expandCollapse" data-test="expand-collapse" onClick={toggleExpand} className={styles.expand_btn}>
					{expanded ? <FiChevronUp size={24} color={colorPalette.text.primary} /> : <FiChevronDown size={24} color={colorPalette.text.primary} />}
				</div>
			</div>
			<div className={expanded ? styles.fieldsContainer : styles.fieldsContainer_collapsed} data-test="fields-container">
				{children}
			</div>
		</div>
	);
}

export default FormInstance;
